import React, { Component } from 'react'
import { Redirect } from 'react-router-dom'
import { connect } from 'react-redux'
import * as debounce from 'lodash.debounce'
import {
  Button,
  Div,
  Footer,
  H3,
  Loader,
  Modal,
  P,
  Section,
  Span,
  Input,
} from '@driftt/tide-core'
import { Table } from '@driftt/tide-table'
import { propEq, propOr } from 'ramda'
import { showTransientNotification, tracking } from '@driftt/traffic-control'
import classnames from 'classnames'

import { deleteSegment } from 'api/segments'
import SegmentColumns from 'constants/all_segments'
import Paginator from 'components/Paginator'
import RowFormatter from './RowFormatter'

import './style.css'

const Loading = ({ hasFinished }) => (
  <Div
    className={classnames('accounts-table-loader', {
      'accounts-table-loader--loaded': hasFinished,
    })}
  >
    <Span>Getting your accounts</Span>
    <Loader className='accounts-table-loader-animation' />
  </Div>
)

class Segments extends Component {
  constructor(props) {
    super(props)

    this.state = {
      disableNext: false,
      disablePrevious: true,
      disableAll: false,
      index: 0,
      pageSize: 25,
      updatedBy: null,
      redirectTo: null,
      deleteConfirmationModalOpen: false,
      segmentIdToDelete: null,
      segmentNameFilter: '',
    }

    this.debouncedHandleSearch = debounce(this.handleSearch, 500)
  }

  getSegmentsForPage = () => {
    const { getSegments } = this.props
    const { index, pageSize, segmentNameFilter } = this.state

    getSegments({
      index,
      size: pageSize,
      nameFilter: segmentNameFilter,
    })
  }

  handleSearch = (event) => {
    this.setState(() => {
      return {
        segmentNameFilter: event.target.value,
      }
    }, this.getSegmentsForPage)
  }

  goToPrevious = () => {
    this.setState((prevState) => {
      const newIndex = prevState.index - prevState.pageSize
      return {
        index: newIndex,
      }
    }, this.getSegmentsForPage)
  }

  goToNext = () => {
    this.setState((prevState) => {
      const newIndex = prevState.index + prevState.pageSize
      return {
        index: newIndex,
      }
    }, this.getSegmentsForPage)
  }

  setPageSize = ({ value: newPageSize }) => {
    const { index } = this.state
    const { total: totalNumberOfSegments } = this.getData()

    let newIndex = index
    if (newPageSize > totalNumberOfSegments) {
      newIndex = 0
    }

    this.setState(
      {
        pageSize: newPageSize,
        index: newIndex,
      },
      this.getSegmentsForPage
    )
  }

  getCurrentPage = () => {
    const { index, pageSize } = this.state

    return Math.floor(index / pageSize)
  }

  setSegmentRedirect = (id) => {
    this.setState({
      redirectTo: id,
    })
  }

  getData = () => {
    const { data } = this.props
    return data ? data : { segments: [], total: 0, totalResults: 0 }
  }

  getTotalNumberOfPages = () => {
    const { pageSize } = this.state

    const { total: totalNumberOfSegments } = this.getData()

    const totalPages = Math.ceil(totalNumberOfSegments / pageSize)
    return totalPages
  }

  getSegmentNames = () => {
    const { data } = this.props
    const { segments } = data

    if (!segments) {
      return []
    }

    return segments.map((segment) => segment.name)
  }

  getSegmentNameById = (id) => {
    const { data } = this.props

    const defaultName = id

    if (!data.segments) {
      return defaultName
    }

    const segment = data.segments.find(propEq('id', id))
    return propOr(defaultName, 'name', segment)
  }

  deleteSegment = async (segmentId) => {
    const {
      showDeleteSuccessNotification,
      showDeleteFailureNotification,
      onDeleteSegment,
    } = this.props

    tracking.trackEvent('Manage Accounts - Delete Segment Button Clicked')

    try {
      await deleteSegment(segmentId)
      showDeleteSuccessNotification()
      onDeleteSegment(segmentId)
    } catch (e) {
      console.warn(e)
      showDeleteFailureNotification()
    } finally {
      this.hideDeleteConfirmationModal()
    }
  }

  renderDeleteConfirmationModal = () => {
    const { deleteConfirmationModalOpen, segmentIdToDelete } = this.state

    if (!deleteConfirmationModalOpen) {
      return null
    }

    if (!segmentIdToDelete) {
      console.warn('Expected a segmentId to be supplied.')
      return null
    }

    return (
      <Modal
        open={deleteConfirmationModalOpen}
        onClose={this.hideDeleteConfirmationModal}
        type='warning'
        size='small'
        className='segment-controls-delete-segment-modal'
      >
        <Div className='segment-controls-delete-segment-modal-body'>
          <H3>Are you sure?</H3>
          <P>
            You are about to delete the segment "
            {this.getSegmentNameById(segmentIdToDelete)}."
          </P>
          <P>This action cannot be undone.</P>
        </Div>
        <Footer>
          <Button
            onClick={() => this.deleteSegment(segmentIdToDelete)}
            type='secondary'
          >
            Delete Segment
          </Button>
          <Button onClick={this.hideDeleteConfirmationModal} type='tertiary'>
            Cancel
          </Button>
        </Footer>
      </Modal>
    )
  }

  handleDeleteButtonClicked = (segmentId) => {
    this.setState({
      deleteConfirmationModalOpen: true,
      segmentIdToDelete: segmentId,
    })
  }

  hideDeleteConfirmationModal = () => {
    this.setState({
      deleteConfirmationModalOpen: false,
      segmentIdToDelete: null,
    })
  }

  render() {
    const { isLoading, onSegmentSelect, users } = this.props
    const { pageSize, redirectTo } = this.state
    const { segments } = this.getData()

    if (!!redirectTo) return <Redirect to={`/segments/${redirectTo}`} />

    return (
      <Div className='segmentation-container'>
        <Section className='segmentation-table-toolbar'>
          <Div>
            <Input
              placeholder='Search segment names'
              onChange={(evt) => {
                evt.persist()
                this.debouncedHandleSearch(evt)
              }}
              type='search'
            />
          </Div>
        </Section>
        {this.renderDeleteConfirmationModal()}

        <Loading hasFinished={!isLoading} />

        {!isLoading && (
          <>
            <Div className='segmentation-table-container'>
              <Table
                className='segmentation-table'
                columns={SegmentColumns}
                data={RowFormatter(
                  segments,
                  onSegmentSelect,
                  users,
                  this.setSegmentRedirect,
                  this.handleDeleteButtonClicked
                )}
              />
            </Div>
            <Paginator
              totalPages={this.getTotalNumberOfPages()}
              size={pageSize}
              index={this.getCurrentPage()}
              goForward={this.goToNext}
              goBackward={this.goToPrevious}
              changeSize={this.setPageSize}
            />
          </>
        )}
      </Div>
    )
  }
}

const mapStateToProps = (state) => {
  return {
    users: state.org.users,
  }
}

const mapDispatchToProps = (dispatch) => ({
  showDeleteSuccessNotification: (message) =>
    dispatch(
      showTransientNotification({
        type: 'success',
        message: 'The segment has been successfully deleted.',
      })
    ),
  showDeleteFailureNotification: () =>
    dispatch(
      showTransientNotification({
        type: 'error',
        message: 'We had a problem deleting this segment.',
      })
    ),
})

export default connect(mapStateToProps, mapDispatchToProps)(Segments)
